import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTeamMembers } from '@/hooks/useTeamManagement';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { UserPlus, X } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface AssignJobRecruiterSelectProps {
  jobId: string;
  assignedTo: string | null;
  compact?: boolean;
}

export function AssignJobRecruiterSelect({ jobId, assignedTo, compact }: AssignJobRecruiterSelectProps) {
  const [open, setOpen] = useState(false);
  const { data: members } = useTeamMembers();
  const { role } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();

  const isAdmin = role === 'org_owner' || role === 'org_admin';

  const mutation = useMutation({
    mutationFn: async (userId: string | null) => {
      const { error } = await supabase
        .from('jobs')
        .update({ default_assignee: userId })
        .eq('id', jobId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jobs'] });
      toast({ title: 'Assignment updated' });
      setOpen(false);
    },
    onError: () => {
      toast({ title: 'Failed to update assignment', variant: 'destructive' });
    },
  });

  const assignedMember = members?.find((m) => m.user_id === assignedTo);
  const initials = assignedMember?.full_name?.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();

  if (!isAdmin) {
    if (!assignedMember) return null;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Avatar className="h-5 w-5 rounded-md">
              <AvatarFallback className="rounded-md bg-primary/10 text-primary text-[9px] font-bold">{initials}</AvatarFallback>
            </Avatar>
            {!compact && <span className="truncate max-w-[100px]">{assignedMember.full_name}</span>}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">Assigned to {assignedMember.full_name}</TooltipContent>
      </Tooltip>
    );
  }

  const eligibleMembers = (members || []).filter(
    (m) => m.status === 'active' && ['org_admin', 'recruiter', 'hiring_manager'].includes(m.role)
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {assignedMember ? (
          <button className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors rounded-md px-1.5 py-0.5 hover:bg-muted/60">
            <Avatar className="h-5 w-5 rounded-md">
              <AvatarFallback className="rounded-md bg-primary/10 text-primary text-[9px] font-bold">{initials}</AvatarFallback>
            </Avatar>
            {!compact && <span className="truncate max-w-[100px]">{assignedMember.full_name}</span>}
          </button>
        ) : (
          <button className="inline-flex items-center gap-1 text-[10px] text-muted-foreground/60 hover:text-muted-foreground transition-colors rounded-md px-1.5 py-0.5 hover:bg-muted/60">
            <UserPlus className="w-3.5 h-3.5" />
            {!compact && <span>Assign</span>}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <p className="text-xs font-semibold text-foreground px-2 py-1.5">Assign to</p>
        <div className="space-y-0.5 max-h-48 overflow-y-auto">
          {eligibleMembers.map((m) => (
            <button
              key={m.user_id}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-muted/60 transition-colors ${m.user_id === assignedTo ? 'bg-primary/10 text-primary font-medium' : 'text-foreground'}`}
              onClick={() => mutation.mutate(m.user_id)}
              disabled={mutation.isPending}
            >
              <Avatar className="h-6 w-6 rounded-md">
                <AvatarFallback className="rounded-md bg-muted text-muted-foreground text-[10px] font-bold">
                  {m.full_name?.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="text-start truncate">
                <span className="text-xs">{m.full_name}</span>
              </div>
            </button>
          ))}
        </div>
        {assignedTo && (
          <button
            className="w-full flex items-center gap-2 px-2 py-1.5 mt-1 rounded-md text-xs text-destructive hover:bg-destructive/10 transition-colors border-t border-border"
            onClick={() => mutation.mutate(null)}
            disabled={mutation.isPending}
          >
            <X className="w-3.5 h-3.5" /> Unassign
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
